import React, { useState, useEffect } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import TextField from '../components/ui-kit/components/TextField';
import useDebounce from '../hooks/useDebounce';

/**
 * SearchInput component with debounce functionality using useDebounce hook
 * @param {Object} props
 * @param {string} props.placeholder - Placeholder text for the search input
 * @param {function} props.onSearch - Callback function to handle search
 * @param {number} props.debounceTime - Debounce delay in milliseconds
 * @param {string} props.defaultValue - Default value for the search input
 */
function SearchInput({ onSearch, debounceTime = 500, defaultValue = '', ...props }) {
  const [searchText, setSearchText] = useState(defaultValue);

  // Create debounced search function using the useDebounce hook
  const debouncedSearch = useDebounce(onSearch, debounceTime);

  // Handle input change
  const handleChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  // Handle clear input
  const handleClear = () => {
    setSearchText('');
    onSearch(''); // Call immediately when clearing
  };

  // Update local state when defaultValue changes (e.g. when filters are cleared)
  useEffect(() => {
    setSearchText(defaultValue);
  }, [defaultValue]);

  return (
    <TextField
      value={searchText}
      onChange={handleChange}
      {...props}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: searchText ? (
          <InputAdornment position="end">
            <IconButton
              aria-label="clear search"
              onClick={handleClear}
              edge="end"
              size="small"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}

export default SearchInput;
